import { useQuery } from '@tanstack/react-query'
import { ArrowUpRight, Shuffle } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toApiError } from '@/api/errors'
import { FormField } from '@/components/forms/FormField'
import { toast } from '@/components/feedback/toast-store'
import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { Dialog } from '@/components/ui/Dialog'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { yearsQuery } from '@/features/academic/api'
import { useChangeSection, usePromoteStudent } from '../api'
import type { StudentDetail } from '../types'
import { SectionPicker } from './SectionPicker'

interface Props {
  student: StudentDetail
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function PromoteDialog({ student, open, onOpenChange }: Props) {
  const { t } = useTranslation('students')
  const years = useQuery(yearsQuery())
  const promote = usePromoteStudent()
  const current = student.summary.currentEnrollment
  const [yearId, setYearId] = useState('')
  const [classId, setClassId] = useState('')
  const [sectionId, setSectionId] = useState('')
  const [roll, setRoll] = useState('')
  const [error, setError] = useState<string | null>(null)

  const otherYears = (years.data ?? []).filter((y) => y.id !== current?.academicYearId)

  const submit = async () => {
    if (!yearId || !sectionId) {
      setError(t('Choose the academic year, class and section.'))
      return
    }
    try {
      await promote.mutateAsync({
        id: student.summary.id,
        academicYearId: Number(yearId),
        sectionId: Number(sectionId),
        rollNumber: roll || null,
      })
      toast.success(
        t('Enrollment updated'),
        t('{{name}} moved to the new academic year.', { name: student.summary.fullName }),
      )
      onOpenChange(false)
    } catch (e) {
      setError(toApiError(e).message)
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      icon={<ArrowUpRight />}
      title={t('Promote to next year')}
      description={
        current
          ? current.sectionName
            ? t('Currently in {{className}} · {{sectionName}} ({{year}}).', {
                className: current.className,
                sectionName: current.sectionName,
                year: current.academicYear,
              })
            : t('Currently in {{className}} ({{year}}).', {
                className: current.className,
                year: current.academicYear,
              })
          : undefined
      }
      dismissible={!promote.isPending}
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            {t('Cancel')}
          </Button>
          <Button onClick={() => void submit()} loading={promote.isPending}>
            {t('Promote')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error ? <Alert tone="danger">{error}</Alert> : null}
        <FormField
          label={t('Academic year')}
          required
          hint={t('Choosing the same class again records the student as repeating the year.')}
        >
          <Select
            placeholder={t('Choose a year')}
            value={yearId}
            onChange={(e) => setYearId(e.target.value)}
            options={otherYears.map((y) => ({ value: y.id, label: y.name }))}
          />
        </FormField>
        <SectionPicker
          classId={classId}
          sectionId={sectionId}
          onClassChange={setClassId}
          onSectionChange={setSectionId}
          showOccupancy={false}
        />
        <FormField label={t('Roll number')}>
          <Input value={roll} onChange={(e) => setRoll(e.target.value)} />
        </FormField>
      </div>
    </Dialog>
  )
}

export function ChangeSectionDialog({ student, open, onOpenChange }: Props) {
  const { t } = useTranslation('students')
  const change = useChangeSection()
  const current = student.summary.currentEnrollment
  const [classId, setClassId] = useState(current ? String(current.classId) : '')
  const [sectionId, setSectionId] = useState(current ? String(current.sectionId) : '')
  const [roll, setRoll] = useState(current?.rollNumber ?? '')
  const [error, setError] = useState<string | null>(null)

  const submit = async () => {
    if (!sectionId) {
      setError(t('Choose a section.'))
      return
    }
    try {
      await change.mutateAsync({
        id: student.summary.id,
        sectionId: Number(sectionId),
        rollNumber: roll || null,
      })
      toast.success(t('Placement updated'))
      onOpenChange(false)
    } catch (e) {
      setError(toApiError(e).message)
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      icon={<Shuffle />}
      title={t('Change section or roll number')}
      description={current ? t('Within {{year}}.', { year: current.academicYear }) : undefined}
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            {t('Cancel')}
          </Button>
          <Button onClick={() => void submit()} loading={change.isPending}>
            {t('Save placement')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error ? <Alert tone="danger">{error}</Alert> : null}
        <SectionPicker
          classId={classId}
          sectionId={sectionId}
          onClassChange={setClassId}
          onSectionChange={setSectionId}
        />
        <FormField label={t('Roll number')}>
          <Input value={roll} onChange={(e) => setRoll(e.target.value)} />
        </FormField>
      </div>
    </Dialog>
  )
}
